import {
  useIsUnifiedFieldsEnabled,
  useShouldRemoveFileUploadPrefix,
} from '@modules/common/hooks';
import { Box, Stack, Tooltip, TooltipProps, Typography } from '@mui/material';
import { EqlField, IntegrationChannelType } from 'generated/graphql';
import { ReactElement } from 'react';

interface EqlFieldCommonMetadataSourceListProps {
  eqlField: EqlField;
  children: ReactElement;
  placement?: TooltipProps['placement'];
}

export const EqlFieldCommonMetadataSourceListTooltip = ({
  eqlField,
  children,
  placement = 'top',
}: EqlFieldCommonMetadataSourceListProps) => {
  const isCommonMetadataField = eqlField.detail?.metadata?.source === 'Common';
  const shouldRemoveFileUploadPrefix = useShouldRemoveFileUploadPrefix();
  const isUnifiedFieldEnabled = useIsUnifiedFieldsEnabled();

  if (!isCommonMetadataField) {
    return null;
  }

  const metadataReferences = eqlField.detail?.metadata?.references;

  if (!metadataReferences?.length) {
    return null;
  }

  const getTooltipTitle = () => {
    if (isUnifiedFieldEnabled) {
      return null;
    }

    return (
      <Stack spacing={1}>
        <Typography variant="caption">
          "{eqlField.displayName}" derived from:
        </Typography>
        <Stack spacing={0.8}>
          {metadataReferences.map((metadataReference) => {
            const sourceName =
              (shouldRemoveFileUploadPrefix
                ? metadataReference.source.replaceAll(
                    `${IntegrationChannelType.FileUpload}-`,
                    '',
                  )
                : metadataReference.source
              ).trim() ?? '';

            return (
              <Typography
                key={metadataReference.key}
                variant="caption"
                lineHeight="16px"
                display="flex"
                component={Box}
              >
                <Box>
                  <Box
                    component="img"
                    width="16px"
                    height="auto"
                    src={metadataReference.iconURL}
                    alt={sourceName}
                    loading="lazy"
                    sx={{ mr: 0.5, mt: -0.2 }}
                  />
                </Box>
                <Box>
                  {metadataReference.displayName} ({sourceName})
                </Box>
              </Typography>
            );
          })}
        </Stack>
      </Stack>
    );
  };

  return (
    <Tooltip arrow title={getTooltipTitle()} placement={placement}>
      {children}
    </Tooltip>
  );
};
